"use client";

import { useRef, useState } from "react";
import { Share2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ScoreSummary } from "@/lib/data/score-summary";
import { SITE_URL } from "@/lib/config";

const SITE_DOMAIN = SITE_URL.replace(/^https?:\/\//, "");

function scoreColor(score: number): string {
  if (score >= 66) return "#3F7A5B";
  if (score >= 33) return "#C6A15B";
  return "#B4544C";
}

/**
 * Teilbare "Top 10 der Woche"-Grafik (Feedback-Runde 4, Punkt 7) - gleiches
 * Königsblau/Gold-Branding und html-to-image-Verfahren wie ScoreCardExport, hier als
 * Rangliste statt Einzel-Score-Ring. Eigene, feste Hex-Farben statt CSS-Variablen aus
 * demselben Grund wie dort (exportierte Grafik soll unabhaengig vom Farbmodus gleich
 * aussehen).
 */
export function Top10Export({ stocks }: { stocks: ScoreSummary[] }) {
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  async function handleDownload() {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Dynamischer statt Top-Level-Import, siehe chart-card.tsx fuer die Begruendung.
      const { toPng } = await import("html-to-image");
      if (document.fonts?.ready) await document.fonts.ready;
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true });
      const link = document.createElement("a");
      link.download = "erikonomie-top10-der-woche.png";
      link.href = dataUrl;
      link.click();
    } catch {
      // Reine Komfortfunktion - bei Fehlschlag passiert einfach nichts.
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <div
        ref={cardRef}
        className="flex w-[400px] flex-col gap-5 rounded-3xl p-8"
        style={{ backgroundColor: "#16265C" }}
      >
        <div className="flex flex-col items-center gap-2">
          <img src="/logo.png" alt="" width={40} height={40} style={{ height: 40, width: 40 }} />
          <span className="wordmark text-sm uppercase" style={{ color: "#E8C784" }}>
            Erikonomie
          </span>
          <span className="text-xs uppercase tracking-[0.15em]" style={{ color: "#C6A15B" }}>
            Top 10 der Woche
          </span>
        </div>

        <div className="flex flex-col gap-2.5">
          {stocks.map((s, i) => (
            <div key={s.symbol} className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-right font-tabular text-sm" style={{ color: "#C6A15B" }}>
                {i + 1}
              </span>
              {s.logoUrl ? (
                <img
                  src={s.logoUrl}
                  alt=""
                  width={24}
                  height={24}
                  style={{
                    height: 24,
                    width: 24,
                    borderRadius: 9999,
                    backgroundColor: "#fff",
                    objectFit: "contain",
                    padding: 2,
                  }}
                />
              ) : (
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
                  style={{ backgroundColor: "#26407F", color: "#FAF9F5" }}
                >
                  {s.symbol.slice(0, 2)}
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-sm" style={{ color: "#FAF9F5" }}>
                {s.name}
              </span>
              <span className="font-tabular text-sm font-medium" style={{ color: scoreColor(s.score) }}>
                {Math.round(s.score)}
              </span>
            </div>
          ))}
        </div>

        <div
          className="w-full pt-4 text-center text-[0.6rem] leading-relaxed"
          style={{ borderTop: "1px solid #26407F", color: "#9DB0D6" }}
        >
          Meistbeobachtete Aktien in der Erikonomie-Community · Bildungswerkzeug, keine
          Anlageberatung
          <br />
          {SITE_DOMAIN}
        </div>
      </div>

      <Button type="button" onClick={handleDownload} disabled={downloading} className="gap-1.5">
        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
        Als Bild teilen
      </Button>
    </div>
  );
}
